/** Named HTML entities that show up in TestRail rich-text fields. Numeric entities are handled
 *  separately before this map is consulted. */
const NAMED_ENTITIES: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
};

/** Closing block tags whose boundary should become a newline so multi-block content stays readable. */
const BLOCK_CLOSE = /<\/(?:p|div|li|h[1-6]|tr|blockquote)>/gi;

/**
 * Only strip tags with a KNOWN HTML element name — never an arbitrary `<...>` span. This preserves
 * plain-text angle brackets like `<username>` placeholders or `count < 5 and total > 0`, which a
 * naive `<[^>]+>` would delete (and which would then be persisted back to the TMS on apply).
 */
const KNOWN_TAG =
  /<\/?(?:p|div|span|br|ol|ul|li|h[1-6]|strong|em|b|i|u|s|a|table|thead|tbody|tfoot|tr|td|th|blockquote|pre|code|img|hr|figure|figcaption|sub|sup|mark|small)\b[^>]*>/gi;

/** Decode a numeric character reference, keeping the original text if the value is not a valid
 *  Unicode scalar (out of range or a surrogate) so a malformed entity can't throw. */
function decodeCodePoint(original: string, value: number): string {
  if (
    !Number.isInteger(value) ||
    value < 0 ||
    value > 0x10ffff ||
    (value >= 0xd800 && value <= 0xdfff)
  ) {
    return original;
  }
  return String.fromCodePoint(value);
}

/**
 * Convert HTML markup to readable plain text. TestRail stores test-case fields (title, precondition,
 * step content/expected, expected result) as HTML from its rich-text editor — `<p>…</p>`, `<br>`,
 * `&nbsp;`, etc. — so rendering them verbatim shows the raw tags. This turns block boundaries into
 * newlines, removes known HTML tags (leaving plain-text angle brackets intact), and decodes common
 * entities. Pure string work (no DOM), so it runs the same in the browser and tests.
 */
export function htmlToText(input: string | null | undefined): string {
  if (!input) return '';
  return input
    .replace(BLOCK_CLOSE, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(KNOWN_TAG, '')
    .replace(/&#(\d+);/g, (match, dec: string) =>
      decodeCodePoint(match, Number(dec)),
    )
    .replace(/&#x([0-9a-f]+);/gi, (match, hex: string) =>
      decodeCodePoint(match, parseInt(hex, 16)),
    )
    .replace(
      /&nbsp;|&amp;|&lt;|&gt;|&quot;|&apos;/gi,
      (match) => NAMED_ENTITIES[match.toLowerCase()] ?? match,
    )
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
